<template>
  <!-- Portals, above the modules (PL-11, amended 2026-10-17). Each one opens in a new tab on the
       app's own /#/portal/{id} route; nothing shows when there is none for the web. -->
  <nav
    class="fts-portal-nav"
    :class="'fts-portal-nav--' + variant"
    :aria-labelledby="labelId"
    v-if="items.length"
  >
    <div class="fts-portal-nav__label" :id="labelId">Portals</div>
    <a
      v-for="item in items"
      :key="item.id"
      class="fts-portal-nav__item"
      :href="hrefOf(item)"
      target="_blank"
      rel="noopener"
      :title="item.label"
      :aria-label="item.label"
      :data-portal-id="item.id"
      @click="open(item, $event)"
    >
      <font-awesome-icon v-if="item.icon.fa" :icon="item.icon.fa" fixed-width class="fts-portal-nav__icon" />
      <span v-else class="fts-portal-nav__icon fts-portal-nav__emoji" aria-hidden="true">{{ item.icon.emoji }}</span>
      <span class="fts-portal-nav__name">{{ item.name }}</span>
      <font-awesome-icon :icon="['fas', 'up-right-from-square']" class="fts-portal-nav__out" />
    </a>
  </nav>
</template>

<script>
/**
 * The Portals group in the side menu (docs/integration-portal-link-proposal.md §4.4, §6 in the SVN
 * repo), one component for both shells: `variant` picks the Client drawer's look or Lite's.
 *
 * The list is the workspace bootstrap's `portals`, already narrowed by the server to what this
 * member may see; webPortals() keeps the ones shown on the web.
 */
import { authorizationStore } from "../store/authorizationStore";
import { webPortals, portalHref } from "./portalNav";

let uid = 0;

export default {
  name: "FTSPortalNav",
  props: {
    workspace: { type: Object, default: null },
    variant: { type: String, default: "client" },
  },
  data() {
    // The nav is named by its own label; the id is per instance.
    uid += 1;
    return { labelId: "fts-portal-nav-label-" + uid };
  },
  computed: {
    items() {
      return webPortals(this.workspace, { runAs: Boolean(authorizationStore.getters.isRunAs) });
    },
  },
  methods: {
    hrefOf(item) {
      return portalHref(item.id, null, this.workspace && this.workspace.id);
    },
    /**
     * Synchronous, with nothing awaited before window.open, so no popup blocker trips. `noopener`:
     * the portal never holds a handle on this tab. A modified click (Ctrl/Cmd/Shift) is left to the
     * browser, which opens the same link its own way.
     */
    open(item, event) {
      if (event && (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)) return;
      if (event && event.preventDefault) event.preventDefault();
      window.open(portalHref(item.id, null, this.workspace && this.workspace.id), "_blank", "noopener");
    },
  },
};
</script>

<style lang="scss" scoped>
.fts-portal-nav {
  display: flex;
  flex-direction: column;
}

.fts-portal-nav__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  text-decoration: none;
  cursor: pointer;

  &:hover,
  &:focus {
    text-decoration: none;
  }
}

.fts-portal-nav__icon {
  flex-shrink: 0;
  width: 1rem;
  text-align: center;
}

.fts-portal-nav__emoji {
  font-size: 0.875rem;
  line-height: 1;
}

.fts-portal-nav__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The "opens in a new tab" mark: there, but quieter than the name. */
.fts-portal-nav__out {
  flex-shrink: 0;
  font-size: 0.625rem;
  opacity: 0.55;
}

.fts-portal-nav__item:hover .fts-portal-nav__out,
.fts-portal-nav__item:focus-visible .fts-portal-nav__out {
  opacity: 0.9;
}

/* ---- Client drawer: the module rows' geometry and the drawer's own colours ---- */
.fts-portal-nav--client {
  padding-bottom: 0.5rem;
  margin-bottom: 0.25rem;
  border-bottom: 1px solid var(--drawer-secondary-border, rgba(0, 0, 0, 0.08));

  .fts-portal-nav__label {
    padding: 0.25rem 0.75rem 0.25rem;
    font-size: 0.6875rem;
    line-height: 1rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--drawer);
    opacity: 0.75;
  }

  .fts-portal-nav__item {
    margin: 0 0.25rem 1px;
    padding: 5px 8px;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--drawer);
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover {
      background-color: var(--drawer-hover-background);
      color: var(--drawer-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--drawer-active);
      outline-offset: -2px;
    }
  }

  .fts-portal-nav__icon {
    opacity: 0.8;
  }
}

/* ---- Lite: the calm-density module rows (32px, ink text, neutral hover). It sits in the sidenav
   header, between the workspace row and the MODULES row, with the label in MODULES' column. ---- */
.fts-portal-nav--lite {
  padding: 8px 0;
  border-bottom: 1px solid var(--fts-gray-100, rgba(0, 0, 0, 0.06));

  .fts-portal-nav__label {
    padding: 4px 16px;
    font-size: 0.625rem;
    line-height: 16px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--fts-form-helper, var(--inistate-text-secondary));
  }

  .fts-portal-nav__item {
    min-height: 32px;
    margin: 0 8px 1px;
    padding: 0 8px;
    border-radius: var(--inistate-rounded-4, 4px);
    font-size: 0.8125rem;
    line-height: 20px;
    color: var(--fts-text, var(--inistate-text-primary-color));

    &:hover {
      background-color: var(--inistate-color-neutral-50);
    }

    &:focus-visible {
      outline: 2px solid var(--inistate-color-primary-500, currentColor);
      outline-offset: -2px;
    }
  }

  .fts-portal-nav__icon {
    color: var(--fts-default, inherit);
  }
}
</style>
